import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import { UserIdLookup } from 'iam/UserIdLookup'
import {
  GetCurrentMember,
  GetCurrentUser,
  GetCurrentWorkspace,
} from 'iam/SessionStore'
import { GetTeamMember } from 'iam/TeamMemberStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { Env } from 'infra/env'
import memoize from 'memoizee'
import { action, computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable } from 'rxjs'
import { Id } from 'types/definitions'
import { assertNotNil } from 'utils/error-util'
import TeamMember from '../iam/models/TeamMember'
import { BillingAPI } from './API'
import { CreateBillingMethodPayload } from './dtos/billing-method'
import { BillingProfileDTO } from './dtos/billing-profile'
import {
  AdjustCreditPayload,
  DepositToCreditPayload,
  BillingTransactionDTO,
} from './dtos/billing-transaction'
import BillingMethod from './models/BillingMethod'
import BillingProfile from './models/BillingProfile'
import BillingTransaction from './models/BillingTransaction'

export type FetchBillingTransactions = (
  ids: string[]
) => Promise<BillingTransaction[]>

export default class BillingStore {
  billingProfiles = collection<BillingProfileDTO, BillingProfile>({
    create: () =>
      new BillingProfile(
        this.getCurrentMember,
        this.getTeamMember,
        this.primeWorkspace,
        this.deleteBillingMethod.bind(this)
      ),
  })

  billingTransactions = collection<BillingTransactionDTO, BillingTransaction>({
    create: () => new BillingTransaction(),
  })

  constructor(
    private api: BillingAPI,
    private getCurrentWorkspace: GetCurrentWorkspace,
    private getCurrentUser: GetCurrentUser,
    private getCurrentMember: GetCurrentMember,
    private getTeamMember: GetTeamMember,
    private primeWorkspace: PrimeWorkspace,
    private fetchUserTeamMembers: () => Promise<TeamMember[]>,
    private userIdLookup: UserIdLookup,
    currentMember$: Observable<Member>
  ) {
    makeObservable(this)
    currentMember$.subscribe(async () => {
      this.fetchBillingProfileForIndividual.clear()
      // this.billingProfiles.clear()
    })
  }

  @computed
  get billingEnabledForCurrentWorkspace() {
    const ws = this.getCurrentWorkspace()
    return ws.features.billing.enabled
  }

  individualBillingProfile(member: Member) {
    return (
      this.billingProfiles.find(
        (p) => p.workspace === member.workspace && p.userId === member.user.id
      ) ?? null
    )
  }

  billingMethodsForIndividual(member: Member) {
    const profile = this.individualBillingProfile(member)
    return profile ? profile.billingMethods.slice() : []
  }

  teamBillingProfile(team: Team) {
    return this.billingProfiles.find((x) => x.teamId === Id.assert(team.id))
  }

  billingMethodsForTeam(team: Team) {
    const profile = this.teamBillingProfile(team)
    return profile ? profile.billingMethods.slice() : []
  }

  fetchBillingProfileForIndividual = memoize(
    async (workspaceId: Id, userPublicId: Id): Promise<BillingProfile> => {
      return this.api.billing
        .getMemberBillingProfile(
          Id.assert(workspaceId),
          Id.assert(userPublicId)
        )
        .then((data) => this.billingProfiles.set(data))
    },
    { promise: true, length: 2 }
  )

  fetchBillingProfileForTeam = memoize(
    async (teamId: string): Promise<BillingProfile> => {
      return this.api.billing.getTeamBillingProfile(teamId).then((data) => {
        return this.billingProfiles.set(data)
      })
    },
    { promise: true, length: 1 }
  )

  async fetchBillingMethodsForTeam(team: Team) {
    const profile = await this.fetchBillingProfileForTeam(Id.assert(team.id))
    return profile.billingMethods.slice()
  }

  async fetchBillingMethodsForIndividual(member: Member) {
    const profile = await this.fetchBillingProfileForIndividual(
      Id.assert(member.workspace.id),
      Id.assert(member.user.publicId)
    )
    return profile.billingMethods.slice()
  }

  @action.bound
  resetBillingProfiles() {
    this.fetchBillingProfileForIndividual.clear()
    this.fetchBillingProfileForTeam.clear()
  }

  changeDefaultBillingMethod(method: BillingMethod, team?: Team | null) {
    const payload = {
      default_billing_method_id: Id.assert(method.id),
    }
    const promise = team
      ? this.api.billing.updateTeamBillingProfile(Id.assert(team.id), payload)
      : this.api.billing.updateMemberBillingProfile(
          Id.assert(method.workspaceId),
          payload,
          Id.assert(this.getCurrentUser().publicId)
        )

    return promise.then((data) => this.billingProfiles.set(data))
  }

  filterBillingTransactionsByIds(ids: string[]) {
    ids = ids.filter((x) => !isNaN(parseInt(x)))
    return this.billingTransactions.filter((x) => ids.includes(Id.assert(x.id)))
  }

  @task.resolved
  fetchBillingTransactions(ids: string[]) {
    ids = ids.filter((x) => !isNaN(parseInt(x)))

    if (ids.length === 0) {
      return Promise.resolve([])
    }

    return this.api.billing
      .getTransactionsByIds({ ids })
      .then((transactions) => this.billingTransactions.set(transactions))
  }

  redirectToStripeConnect() {
    const workspace = this.getCurrentWorkspace()
    const ROOT = `${window.location.protocol}//${window.location.host}/callback/stripe-connect`
    window.location.href = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${
      workspace.config?.billing?.stripe_connect_id || Env.STRIPE_CONNECT_ID
    }&scope=read_write&redirect_uri=${ROOT}`
  }

  async addBillingMethodForStripeToken(
    member: Member,
    team: Team | undefined | null,
    token: string
  ): Promise<BillingMethod> {
    const profile = team
      ? await this.fetchBillingProfileForTeam(Id.assert(team.id))
      : await this.fetchBillingProfileForIndividual(
          member.workspace.id,
          member.user.publicId
        )

    const payload: CreateBillingMethodPayload = {
      type: 'Stripe',
      user_id: profile.userPublicId,
      workspace_id: Id.assert(profile.workspace.id),
      token,
      team_id: team ? team.id : undefined,
    }
    return this.api.billing
      .addBillingMethod(payload)
      .then((data) => profile.billingMethods.set(data))
  }

  async deleteBillingMethod(method: BillingMethod) {
    if (method.default) {
      return
    }
    return this.api.billing
      .removeBillingMethod(Id.assert(method.id))
      .then(() =>
        method.billingProfile.billingMethods.remove(Id.assert(method.id))
      )
  }

  async depositCredit(billingMethodId: string, data: DepositToCreditPayload) {
    const profile = assertNotNil(
      this.billingProfiles.find((p) => !!p.billingMethods.get(billingMethodId)),
      'Cannot deposit credit because the billing profile was not loaded.'
    )
    await this.api.billing
      .depositCredit(billingMethodId, data)
      .then((data) => profile.billingMethods.set(data))
  }

  async adjustCredit(billingMethodId: string, data: AdjustCreditPayload) {
    const profile = assertNotNil(
      this.billingProfiles.find((p) => !!p.billingMethods.get(billingMethodId)),
      'Cannot adjust credit because the billing profile was not loaded.'
    )
    await this.api.billing
      .adjustCredit(billingMethodId, data)
      .then((data) => profile.billingMethods.set(data))
  }
}
